import {
  addEntry,
  getAllEntries,
  getEntry,
  getPendingEntries,
  markBlockedByPeticao,
  removeCompletedEntries,
  removeEntry,
  unblockPeticao,
  updateEntryStatus,
  type DeleteAnexoPeticaoPayload,
  type GenericHttpPayload,
  type OutboxEntry,
  type PatchAnexoPeticaoPayload,
  type PatchRascunhoPeticaoPayload,
  type PeticaoFullFlowPayload,
  type ReordenarAnexosPeticaoPayload,
  type SubmitPeticaoOutboxPayload,
  type UploadAnexoPeticaoPayload,
  type UploadPrincipalPeticaoPayload,
} from './outbox';
import { api } from '@/api/client';
import { getAtuacao } from '@/lib/delegacao/atuacao';
import {
  confirmAnexo,
  confirmPrincipal,
  createRascunho,
  deleteAnexo,
  fetchPeticao,
  patchAnexo,
  patchRascunho,
  presignAnexo,
  presignPrincipal,
  reordenarAnexos,
  sha256OfBlob,
  submitPeticao,
  uploadBlobToS3,
} from '@/api/peticoes';
import type { Peticao, PeticaoAnexo } from '@/components/peticionamento/types';
import { classifyError } from '@/lib/errors';
import { deleteBlob, getBlob } from './pendingPeticaoBlobs';
import { getSyncLockName, getUserIdOrNull } from './userScope';
export const MAX_RETRIES = 5;

/**
 * Emite evento global quando uma entry transita para ``failed`` — a UI
 * (BarraStatusFormulario, toasts, etc.) consome via ``window.addEventListener``
 * para mostrar feedback rico sem polling no outbox.
 *
 * ``determinístico=true`` indica falha 4xx ≠ 401/403 (retry não vai resolver,
 * usuário precisa intervir). ``false`` indica esgotamento de tentativas
 * (transitório que persistiu).
 */
function emitSyncFalha(
  entry: OutboxEntry,
  mensagem: string,
  determinístico: boolean,
): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent('peticionar:sync-falha', {
        detail: {
          entryId: entry.id,
          type: entry.type,
          peticaoId: entry.payload.peticao_id ?? null,
          mensagem,
          determinístico,
        },
      }),
    );
  } catch {
    // Listener faltoso ou ambiente sem CustomEvent não deve quebrar o sync
  }
}

/**
 * Aborts ativos por entry em sincronização. Populado pelos handlers
 * ``sendUploadAnexoPeticao`` / ``sendUploadPrincipalPeticao`` no início do
 * upload e limpo no fim (sucesso ou erro). ``cancelSyncingEntry`` consulta
 * esse map para abortar XHRs em curso.
 */
const inflightAborts = new Map<string, AbortController>();

/**
 * Entries marcadas como canceladas durante ``syncing`` mas que não chegaram
 * a estado terminal antes do deadline. ``processSingleEntry`` consulta esse
 * set ao escrever o status final e respeita o ``failed`` posto pelo
 * cancelamento (não sobrescreve com done/pending). Limpo quando a entry é
 * removida ou o usuário reverte explicitamente.
 */
const cancelledEntries = new Set<string>();

/**
 * Pub/sub direto para sinalizar que uma petição mudou no backend (após
 * dispatch bem-sucedido de uma entry). Mais confiável que observar
 * ``status === 'done'`` na UI, porque ``removeCompletedEntries`` pode
 * apagar a entry antes da UI re-renderizar.
 */
/**
 * Descrição estruturada da mudança feita por uma entry bem-sucedida.
 * Permite que a UI atualize o cache via ``setQueryData`` em vez de
 * disparar um refetch da petição inteira — eliminando o gap visual entre
 * a entry sair do outbox e o snapshot novo chegar.
 *
 * ``unknown`` é o fallback quando o handler não tem como descrever a
 * mudança (ex.: ``peticao_full_flow``, ``submit_peticao_outbox``) — a UI
 * cai para ``invalidateQueries`` e aceita o flicker (raro nesses casos).
 */
export type PeticaoMutationDelta =
  | { kind: 'anexo_added'; anexo: PeticaoAnexo }
  | { kind: 'anexo_removed'; anexoId: string }
  | { kind: 'anexo_updated'; anexo: PeticaoAnexo }
  | { kind: 'anexos_reordered'; anexos: PeticaoAnexo[] }
  | { kind: 'peticao_replaced'; peticao: Peticao }
  | { kind: 'unknown' };

type PeticaoChangeListener = (
  peticaoId: string,
  delta: PeticaoMutationDelta,
) => void;
const peticaoChangeListeners = new Set<PeticaoChangeListener>();

export function subscribePeticaoChange(listener: PeticaoChangeListener): () => void {
  peticaoChangeListeners.add(listener);
  return () => peticaoChangeListeners.delete(listener);
}

function notifyPeticaoChange(peticaoId: string, delta: PeticaoMutationDelta): void {
  for (const cb of peticaoChangeListeners) {
    try {
      cb(peticaoId, delta);
    } catch {
      // listener faltoso não deve quebrar o sync
    }
  }
}

// --- Observable sync state ---
// Immutable snapshot for useSyncExternalStore (stable reference)
let _syncSnapshot: { isSyncing: boolean; lastSyncAt: number | null } = {
  isSyncing: false,
  lastSyncAt: null,
};
const _syncListeners = new Set<() => void>();

function setSyncState(isSyncing: boolean, lastSyncAt?: number | null) {
  _syncSnapshot = {
    isSyncing,
    lastSyncAt: lastSyncAt !== undefined ? lastSyncAt : _syncSnapshot.lastSyncAt,
  };
  _syncListeners.forEach((cb) => cb());
}

export function subscribeSyncState(cb: () => void): () => void {
  _syncListeners.add(cb);
  return () => { _syncListeners.delete(cb); };
}

export function getSyncState(): { isSyncing: boolean; lastSyncAt: number | null } {
  return _syncSnapshot;
}

export interface SyncResult {
  synced: number;
  failed: number;
  remaining: number;
}

/**
 * Sends a blob via FormData to the target endpoint (legacy generic handler).
 */
async function sendBlobPayload(entry: OutboxEntry): Promise<void> {
  const payload = entry.payload as GenericHttpPayload;
  const { method, url, body, blobs } = payload;

  if (!method || !url) {
    throw new Error('Payload inválido: requisição sem método ou URL');
  }

  if (blobs && blobs.length > 0) {
    const formData = new FormData();

    if (body && typeof body === 'object') {
      for (const [key, value] of Object.entries(body as Record<string, unknown>)) {
        formData.append(key, String(value));
      }
    }

    for (const blob of blobs) {
      formData.append(blob.name, blob.data, blob.name);
    }

    await api({
      method,
      url,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  } else {
    await api({
      method,
      url,
      data: body,
    });
  }
}

/**
 * Idempotent full-flow handler for the petition outbox entry.
 *
 * Sequence (each step tolerates being re-run):
 *   1. POST /peticoes/rascunho with draft_key (returns existing if any)
 *   2. PATCH metadata (modo_corpo / corpo_html / tipos)
 *   3. Upload principal if UPLOAD_PDF and not yet confirmed (presign → PUT → confirm; delete blob)
 *   4. For each anexo not yet confirmed on backend: presign → PUT → confirm; delete blob
 *   5. POST /peticoes/{id}/submit with idempotency_key (backend is idempotent on this key)
 */
async function sendPeticaoFullFlow(entry: OutboxEntry): Promise<void> {
  const payload = entry.payload as PeticaoFullFlowPayload;
  const {
    draft_key,
    processo_id,
    idempotency_key,
    metadata,
    anexos,
    principal_file,
    id_fonte_dados_codex,
  } = payload;

  // Step 1: create or reuse draft — repassa a tramitação escolhida quando
  // presente (processos multi-grau). Omitida, o backend resolve default.
  const draft = await createRascunho({
    draft_key,
    processo_id,
    ...(id_fonte_dados_codex ? { id_fonte_dados_codex } : {}),
  });

  // Step 2: patch metadata (idempotent — server re-applies the same values)
  await patchRascunho(draft.id, {
    modo_corpo: metadata.modo_corpo,
    corpo_html: metadata.corpo_html ?? null,
    id_tipo_documento_principal: metadata.id_tipo_documento_principal,
    ...(metadata.documento ? { documento: metadata.documento } : {}),
    ...(metadata.origem_ia !== undefined
      ? { origem_ia: metadata.origem_ia, mensagem_ia_origem_id: metadata.mensagem_ia_origem_id ?? null }
      : {}),
  });

  // Fetch current state after patch to know which uploads already landed
  let current = await fetchPeticao(draft.id);

  // Step 3: principal file (UPLOAD_PDF mode)
  // Sobe o principal somente enquanto o backend ainda não tem o arquivo no
  // bucket jurisapp (status PENDENTE). CONFIRMADO/CONSOLIDADO indicam que o
  // upload local já ocorreu em sessão anterior — não repetir.
  if (
    metadata.modo_corpo === 'UPLOAD_PDF' &&
    principal_file &&
    current.status_upload_principal === 'PENDENTE'
  ) {
    const entry = await getBlob(principal_file.blob_ref);
    if (!entry) {
      throw new Error('Arquivo principal da petição não encontrado no armazenamento local');
    }
    const presign = await presignPrincipal(draft.id, {
      nome_arquivo: principal_file.nome,
      content_type: principal_file.content_type,
      tamanho_bytes: entry.blob.size,
    });
    await uploadBlobToS3(presign, entry.blob, principal_file.nome);
    const sha = await sha256OfBlob(entry.blob);
    await confirmPrincipal(draft.id, {
      s3_key: presign.s3_key,
      sha256: sha,
      nome_arquivo: principal_file.nome,
      content_type: principal_file.content_type,
      tamanho_bytes: entry.blob.size,
    });
    await deleteBlob(principal_file.blob_ref);
    current = await fetchPeticao(draft.id);
  }

  // Step 4: anexos — skip those already confirmed on backend (matched by ordem)
  const confirmedOrdens = new Set(current.anexos.map((a) => a.ordem));
  for (const a of anexos) {
    if (confirmedOrdens.has(a.ordem)) continue;
    const entry = await getBlob(a.blob_ref);
    if (!entry) {
      throw new Error(`Anexo ${a.ordem} não encontrado no armazenamento local`);
    }
    const presign = await presignAnexo(draft.id, {
      nome_arquivo: a.nome,
      content_type: a.content_type,
      tamanho_bytes: entry.blob.size,
    });
    await uploadBlobToS3(presign, entry.blob, a.nome);
    const sha = await sha256OfBlob(entry.blob);
    await confirmAnexo(draft.id, {
      s3_key: presign.s3_key,
      sha256: sha,
      ordem: a.ordem,
      id_tipo_documento: a.id_tipo_documento,
      descricao: a.descricao ?? null,
      nome_arquivo: a.nome,
      content_type: a.content_type,
      tamanho_bytes: entry.blob.size,
      sigiloso: a.sigiloso ?? false,
    });
    await deleteBlob(a.blob_ref);
  }

  // Step 5: submit (backend dedupes on idempotency_key)
  await submitPeticao(draft.id, idempotency_key);
}

// ===========================================================================
// Handlers do upload eager (entries individuais por petição)
// ===========================================================================

/**
 * Resolve uma referência de anexo (``anexo_id`` ou ``client_upload_id``) para
 * o id real do backend, consultando o detalhe da petição quando necessário.
 *
 * PATCH/DELETE/reordenar podem ser enfileirados antes do próprio upload virar
 * ``done``. Nesse momento o frontend só conhece o ``client_upload_id``. Pelo
 * FIFO causal, o upload já terá sucedido quando estes handlers rodarem — o
 * backend devolve o anexo via ``client_upload_id``.
 */
async function resolveAnexoId(
  peticaoId: string,
  ref: { anexo_id?: string; client_upload_id?: string },
): Promise<string> {
  if (ref.anexo_id) return ref.anexo_id;
  if (!ref.client_upload_id) {
    throw new Error('Referência de anexo sem id e sem client_upload_id');
  }
  const peticao = await fetchPeticao(peticaoId);
  const found = peticao.anexos.find(
    (a) => (a as { client_upload_id?: string }).client_upload_id === ref.client_upload_id,
  );
  if (!found) {
    throw new Error('Anexo não encontrado pelo client_upload_id');
  }
  return found.id;
}

async function sendUploadAnexoPeticao(
  entry: OutboxEntry,
): Promise<PeticaoMutationDelta> {
  const payload = entry.payload as UploadAnexoPeticaoPayload;
  const blobEntry = await getBlob(payload.blob_ref);
  if (!blobEntry) {
    throw new Error('Arquivo do anexo não encontrado no armazenamento local');
  }
  const presign = await presignAnexo(payload.peticao_id, {
    nome_arquivo: payload.nome,
    content_type: payload.content_type,
    tamanho_bytes: blobEntry.blob.size,
  });
  const abort = new AbortController();
  inflightAborts.set(entry.id, abort);
  try {
    await uploadBlobToS3(presign, blobEntry.blob, payload.nome, undefined, abort.signal);
    const sha = await sha256OfBlob(blobEntry.blob);
    const anexo = await confirmAnexo(payload.peticao_id, {
      s3_key: presign.s3_key,
      sha256: sha,
      ordem: payload.ordem,
      id_tipo_documento: payload.id_tipo_documento,
      descricao: payload.descricao ?? null,
      tipo_documento_descricao: payload.tipo_documento_descricao ?? null,
      nome_arquivo: payload.nome,
      content_type: payload.content_type,
      tamanho_bytes: blobEntry.blob.size,
      sigiloso: payload.sigiloso ?? false,
      client_upload_id: payload.client_upload_id,
    });
    await deleteBlob(payload.blob_ref);
    return { kind: 'anexo_added', anexo };
  } finally {
    inflightAborts.delete(entry.id);
  }
}

async function sendUploadPrincipalPeticao(
  entry: OutboxEntry,
): Promise<PeticaoMutationDelta> {
  const payload = entry.payload as UploadPrincipalPeticaoPayload;
  const blobEntry = await getBlob(payload.blob_ref);
  if (!blobEntry) {
    throw new Error('Arquivo principal não encontrado no armazenamento local');
  }
  const presign = await presignPrincipal(payload.peticao_id, {
    nome_arquivo: payload.nome,
    content_type: payload.content_type,
    tamanho_bytes: blobEntry.blob.size,
  });
  const abort = new AbortController();
  inflightAborts.set(entry.id, abort);
  try {
    await uploadBlobToS3(presign, blobEntry.blob, payload.nome, undefined, abort.signal);
    const sha = await sha256OfBlob(blobEntry.blob);
    const peticao = await confirmPrincipal(payload.peticao_id, {
      s3_key: presign.s3_key,
      sha256: sha,
      nome_arquivo: payload.nome,
      content_type: payload.content_type,
      tamanho_bytes: blobEntry.blob.size,
      client_upload_id: payload.client_upload_id,
    });
    await deleteBlob(payload.blob_ref);
    return { kind: 'peticao_replaced', peticao };
  } finally {
    inflightAborts.delete(entry.id);
  }
}

async function sendPatchAnexoPeticao(
  entry: OutboxEntry,
): Promise<PeticaoMutationDelta> {
  const payload = entry.payload as PatchAnexoPeticaoPayload;
  const anexoId = await resolveAnexoId(payload.peticao_id, payload);
  const anexo = await patchAnexo(payload.peticao_id, anexoId, payload.patch);
  return { kind: 'anexo_updated', anexo };
}

async function sendDeleteAnexoPeticao(
  entry: OutboxEntry,
): Promise<PeticaoMutationDelta> {
  const payload = entry.payload as DeleteAnexoPeticaoPayload;
  try {
    const anexoId = await resolveAnexoId(payload.peticao_id, payload);
    await deleteAnexo(payload.peticao_id, anexoId);
    return { kind: 'anexo_removed', anexoId };
  } catch (error: unknown) {
    // 404 é sucesso silencioso: o anexo já não existe no backend, o objetivo
    // do DELETE foi atingido. ``resolveAnexoId`` também pode lançar quando o
    // anexo não está mais na petição (mesma semântica — já foi removido).
    // Nesses casos, ``payload.anexo_id`` pode estar presente (caminho normal)
    // ou ausente (cancelamento via client_upload_id) — sem ele, a UI cai
    // para fallback ``unknown`` (refetch). Aceitável, raro.
    const classified = classifyError(error);
    if (classified.status === 404) {
      if (payload.anexo_id) return { kind: 'anexo_removed', anexoId: payload.anexo_id };
      return { kind: 'unknown' };
    }
    if (
      error instanceof Error &&
      error.message === 'Anexo não encontrado pelo client_upload_id'
    ) {
      if (payload.anexo_id) return { kind: 'anexo_removed', anexoId: payload.anexo_id };
      return { kind: 'unknown' };
    }
    throw error;
  }
}

async function sendReordenarAnexosPeticao(
  entry: OutboxEntry,
): Promise<PeticaoMutationDelta> {
  const payload = entry.payload as ReordenarAnexosPeticaoPayload;
  const ordens = await Promise.all(
    payload.ordens.map(async (item) => ({
      anexo_id: await resolveAnexoId(payload.peticao_id, item),
      ordem: item.ordem,
    })),
  );
  const anexos = await reordenarAnexos(payload.peticao_id, ordens);
  return { kind: 'anexos_reordered', anexos };
}

async function sendPatchRascunhoPeticao(
  entry: OutboxEntry,
): Promise<PeticaoMutationDelta> {
  const payload = entry.payload as PatchRascunhoPeticaoPayload;
  const peticao = await patchRascunho(payload.peticao_id, payload.patch);
  return { kind: 'peticao_replaced', peticao };
}

async function sendSubmitPeticaoOutbox(
  entry: OutboxEntry,
): Promise<PeticaoMutationDelta> {
  const payload = entry.payload as SubmitPeticaoOutboxPayload;
  await submitPeticao(payload.peticao_id, payload.idempotency_key);
  // Submit muda status global e potencialmente outros campos (mensagens,
  // próximo passo etc.) — handler não tem visão completa, deixamos a UI
  // refetchar.
  return { kind: 'unknown' };
}

async function dispatchEntry(entry: OutboxEntry): Promise<PeticaoMutationDelta> {
  switch (entry.type) {
    case 'peticao_full_flow':
      await sendPeticaoFullFlow(entry);
      return { kind: 'unknown' };
    case 'upload_anexo_peticao':
      return sendUploadAnexoPeticao(entry);
    case 'upload_principal_peticao':
      return sendUploadPrincipalPeticao(entry);
    case 'patch_anexo_peticao':
      return sendPatchAnexoPeticao(entry);
    case 'delete_anexo_peticao':
      return sendDeleteAnexoPeticao(entry);
    case 'reordenar_anexos_peticao':
      return sendReordenarAnexosPeticao(entry);
    case 'patch_rascunho_peticao':
      return sendPatchRascunhoPeticao(entry);
    case 'submit_peticao_outbox':
      return sendSubmitPeticaoOutbox(entry);
    default:
      await sendBlobPayload(entry);
      return { kind: 'unknown' };
  }
}

/**
 * Chave de agrupamento de uma entry para o sync causal. Petições têm
 * ``payload.peticao_id``; quem precisa de ordem causal declara
 * ``payload.grupo`` (anotações: um grupo por processo); o resto forma grupos
 * individuais.
 */
function groupKey(entry: OutboxEntry): string {
  if (entry.payload.peticao_id) return `peticao:${entry.payload.peticao_id}`;
  if (entry.payload.draft_key) return `draft:${entry.payload.draft_key}`;
  if (entry.payload.grupo) return `grupo:${entry.payload.grupo}`;
  return `entry:${entry.id}`;
}

/**
 * Processa uma entry isolada e atualiza seu status. Retorna o status final
 * — ``done`` (sucesso), ``failed`` (erro determinístico) ou ``pending``
 * (transitório). O caller usa esse retorno para decidir se interrompe o
 * grupo da petição (parada por rodada).
 */
async function processSingleEntry(
  entry: OutboxEntry,
): Promise<'done' | 'failed' | 'pending' | 'blocked'> {
  // Predecessor falho da mesma petição — entries adicionadas DEPOIS do
  // failed entram como pending mas não devem rodar até que a falha seja
  // revertida. Marca como blocked aqui (markBlockedByPeticao só pega
  // entries que existiam na hora da falha; esta verificação cobre
  // entries criadas posteriormente).
  if (entry.payload.peticao_id) {
    const all = await getAllEntries();
    const temPredecessorFalho = all.some(
      (e) =>
        e.payload.peticao_id === entry.payload.peticao_id &&
        e.id !== entry.id &&
        (e.status === 'failed' || e.status === 'blocked'),
    );
    if (temPredecessorFalho) {
      await updateEntryStatus(entry.id, 'blocked', 'Aguardando upload anterior');
      return 'blocked';
    }
  }

  if (entry.retryCount >= MAX_RETRIES && !entry.payload.semTetoDeTentativas) {
    const mensagem = 'Número máximo de tentativas excedido';
    await updateEntryStatus(entry.id, 'failed', mensagem);
    emitSyncFalha(entry, mensagem, false);
    if (entry.payload.peticao_id) {
      await markBlockedByPeticao(entry.payload.peticao_id, 'Falha em entry anterior');
    }
    return 'failed';
  }

  await updateEntryStatus(entry.id, 'syncing');

  try {
    const delta = await dispatchEntry(entry);
    // Se a entry foi cancelada enquanto rodava (cancelSyncingEntry após
    // deadline), o usuário pediu para descartar mesmo que o dispatch
    // tenha completado. Aqui é a única chance de garantir o cleanup do
    // recurso confirmado E de NÃO deixar a entry como obstáculo no FIFO
    // causal (uma entry ``failed`` bloqueia todas as outras da petição,
    // inclusive o próprio delete que estamos enfileirando).
    if (cancelledEntries.has(entry.id)) {
      const peticaoId = entry.payload.peticao_id;
      if (entry.type === 'upload_anexo_peticao') {
        const p = entry.payload as Partial<UploadAnexoPeticaoPayload>;
        if (p.peticao_id && p.client_upload_id) {
          await addEntry({
            type: 'delete_anexo_peticao',
            payload: {
              peticao_id: p.peticao_id,
              client_upload_id: p.client_upload_id,
            },
            description: 'Cancelar anexo já enviado',
          });
        }
      }
      // Para upload_principal_peticao não há endpoint de "remover principal";
      // a substituição cuida do cleanup do S3 anterior no service.

      // Remove a entry original e libera dependentes blocked. A intenção
      // do usuário (cancelar) está atendida: o cleanup foi enfileirado e
      // não há razão para manter um obstáculo failed na petição.
      const blobRef = (entry.payload as { blob_ref?: string }).blob_ref;
      if (blobRef) {
        await deleteBlob(blobRef);
      }
      await removeEntry(entry.id);
      if (peticaoId) {
        await unblockPeticao(peticaoId);
        // Cancelamento: sem delta confiável (o anexo foi confirmado mas
        // já enfileiramos o delete) — UI faz refetch como fallback.
        notifyPeticaoChange(peticaoId, { kind: 'unknown' });
      }
      cancelledEntries.delete(entry.id);
      // ``done`` permite o loop seguir processando o grupo; o cleanup
      // enfileirado roda na próxima rodada.
      return 'done';
    }
    await updateEntryStatus(entry.id, 'done');
    // Propaga o delta estruturado para a UI atualizar o cache via
    // ``setQueryData`` em vez de refetchar a petição inteira — assim
    // não há gap visual entre a entry sair do outbox e o snapshot novo.
    if (entry.payload.peticao_id) {
      notifyPeticaoChange(entry.payload.peticao_id, delta);
    }
    return 'done';
  } catch (error: unknown) {
    // Mesma proteção contra sobrescrita por erro tardio.
    if (cancelledEntries.has(entry.id)) {
      // Upload cancelado falhou (ou foi abortado) — não há recurso para
      // limpar no backend. Remove a entry e libera dependentes.
      const peticaoId = entry.payload.peticao_id;
      const blobRef = (entry.payload as { blob_ref?: string }).blob_ref;
      if (blobRef) {
        await deleteBlob(blobRef);
      }
      await removeEntry(entry.id);
      if (peticaoId) {
        await unblockPeticao(peticaoId);
      }
      cancelledEntries.delete(entry.id);
      return 'done';
    }
    const classified = classifyError(error);
    // 429 (rate limit) é transitório por construção — o servidor pediu para
    // esperar. Lê Retry-After e agenda nova drenagem global após o delay,
    // sem incrementar retryCount nem disparar emitSyncFalha (não é falha do
    // peticionamento, é congestionamento). O grupo atual é interrompido pelo
    // ``break`` no caller — sem isso voltaríamos a estourar imediatamente.
    if (classified.kind === 'rate_limited') {
      const retryAfter = readRetryAfterSeconds(error);
      scheduleRetryAfter(retryAfter);
      await updateEntryStatus(
        entry.id,
        'pending',
        `Aguardando ${retryAfter}s (limite de requisições)`,
      );
      return 'pending';
    }

    // 409 (conflict) no upload eager indica uso indevido de client_upload_id
    // (mesmo id, sha diferente) — é determinístico: retry não vai resolver.
    // 401/403 ficam como transitório (sessão pode renovar).
    const isDeterministic =
      classified.status !== undefined &&
      classified.status >= 400 &&
      classified.status < 500 &&
      classified.kind !== 'auth' &&
      classified.kind !== 'forbidden';

    if (isDeterministic) {
      await updateEntryStatus(entry.id, 'failed', classified.message);
      emitSyncFalha(entry, classified.message, true);
      if (entry.payload.peticao_id) {
        await markBlockedByPeticao(entry.payload.peticao_id, classified.message);
      }
      return 'failed';
    }

    await updateEntryStatus(entry.id, 'pending', classified.message);
    return 'pending';
  }
}

/**
 * Extrai segundos do header ``Retry-After`` (formato delta-seconds, conforme
 * RFC 9110). Aceita resposta axios padrão; cai num default razoável quando o
 * header está ausente ou malformado para evitar tight loop.
 */
function readRetryAfterSeconds(error: unknown): number {
  const DEFAULT_RETRY_SECONDS = 30;
  if (!error || typeof error !== 'object') return DEFAULT_RETRY_SECONDS;
  const response = (error as { response?: { headers?: Record<string, unknown> } }).response;
  const raw = response?.headers?.['retry-after'];
  if (typeof raw !== 'string' && typeof raw !== 'number') return DEFAULT_RETRY_SECONDS;
  const seconds = Number(raw);
  if (!Number.isFinite(seconds) || seconds <= 0) return DEFAULT_RETRY_SECONDS;
  // Cap defensivo em 5 minutos: header malicioso/bug não pode parar a fila por
  // muito tempo; o usuário ainda tem o gatilho manual de "tentar agora".
  return Math.min(Math.ceil(seconds), 300);
}

/**
 * Agenda nova chamada de ``processOutbox`` após ``seconds``. Coalesce: se já
 * há um timer pendente para horizonte semelhante, mantém o existente —
 * evitamos thundering herd quando 5+ entries da mesma rajada batem 429 em
 * sequência. O timer é reset somente se o novo prazo for materialmente menor
 * (>5s de diferença) que o já agendado.
 */
let pendingRetryTimer: ReturnType<typeof setTimeout> | null = null;
let pendingRetryDeadline = 0;

function scheduleRetryAfter(seconds: number): void {
  const deadline = Date.now() + seconds * 1000;
  if (pendingRetryTimer && deadline >= pendingRetryDeadline - 5000) {
    return;
  }
  if (pendingRetryTimer) clearTimeout(pendingRetryTimer);
  pendingRetryDeadline = deadline;
  pendingRetryTimer = setTimeout(() => {
    pendingRetryTimer = null;
    pendingRetryDeadline = 0;
    void processOutbox();
  }, seconds * 1000);
}

/**
 * Antes do primeiro ``processOutbox`` da sessão, reverte entries em
 * ``syncing`` que não têm worker ativo (caso aba fechada no meio do upload).
 * O ``inflightAborts`` map fica vazio em aba nova, então qualquer
 * ``syncing`` é órfão por definição.
 */
async function recoverOrphanedSyncing(): Promise<void> {
  // Varre tudo (não só pending) — entries em ``syncing`` sem worker
  // registrado em ``inflightAborts`` são órfãs (ex.: aba fechada no meio
  // do upload) e precisam voltar para ``pending``.
  const entries = await getAllEntries();
  for (const e of entries) {
    if (e.status === 'syncing' && !inflightAborts.has(e.id)) {
      await updateEntryStatus(e.id, 'pending', 'Recuperação de upload órfão');
    }
  }
}

let recoveryDone = false;

/**
 * Processa o outbox em FIFO causal por petição.
 * Agrupa entries por ``peticao_id`` (ou ``draft_key``); dentro de cada grupo,
 * processa uma entry de cada vez. Qualquer falha (determinística ou
 * transitória) interrompe o grupo nessa rodada — outros grupos continuam.
 *
 * Compete pelo mesmo Web Lock global de ``processOutboxForPeticao`` para
 * evitar paralelismo na mesma petição entre invocações.
 */
export async function processOutbox(): Promise<SyncResult> {
  return runWithLock(undefined);
}

/**
 * Drena entries de uma petição específica desde a primeira ``pending`` até
 * o fim do grupo (FIFO). Usado pelo caminho online imediato após
 * ``addEntry``. Adquire o mesmo lock global de ``processOutbox``.
 */
export async function processOutboxForPeticao(peticaoId: string): Promise<SyncResult> {
  return runWithLock(peticaoId);
}

function doContextoAtivo(entry: OutboxEntry): boolean {
  return (entry.atuandoPor ?? null) === (getAtuacao()?.titularId ?? null);
}

async function runWithLock(filterPeticaoId: string | undefined): Promise<SyncResult> {
  if (!getUserIdOrNull()) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const doSync = async (): Promise<SyncResult> => {
    if (!recoveryDone) {
      await recoverOrphanedSyncing();
      recoveryDone = true;
    }

    // Só as entries do contexto ATIVO: a reprodução usa o interceptor global, que
    // manda (ou não) o ``X-Atuando-Por`` do contexto corrente. Entry de outra conta
    // espera o usuário voltar a ela — nunca é reproduzida na conta errada.
    const all = (await getPendingEntries()).filter(doContextoAtivo);
    const entries = filterPeticaoId
      ? all.filter((e) => e.payload.peticao_id === filterPeticaoId)
      : all;

    if (entries.length === 0) {
      return { synced: 0, failed: 0, remaining: 0 };
    }

    // Agrupa por petição (ou draft) e processa cada grupo em FIFO.
    const groups = new Map<string, OutboxEntry[]>();
    for (const entry of entries) {
      const key = groupKey(entry);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(entry);
    }

    setSyncState(true);
    try {
      let synced = 0;
      let failed = 0;

      for (const [, group] of groups) {
        for (const entry of group) {
          // Reconfere a cada entry: a troca de contexto é recusada enquanto
          // ``isSyncing`` (useTrocarAtuacao), mas a janela antes do
          // ``setSyncState(true)`` existe.
          if (!doContextoAtivo(entry)) break;
          const result = await processSingleEntry(entry);
          if (result === 'done') {
            synced++;
          } else {
            failed++;
            // Parada por rodada: qualquer falha interrompe o grupo. Outros
            // grupos seguem sendo processados.
            break;
          }
        }
      }

      await removeCompletedEntries();
      const remaining = (await getPendingEntries()).length;
      return { synced, failed, remaining };
    } finally {
      setSyncState(false, Date.now());
    }
  };

  if (navigator.locks) {
    // Tenta adquirir sem bloquear; se ocupado, agenda nova tentativa
    // depois que a invocação atual liberar (sem isso, uma entry criada
    // durante um sync ativo pode ficar pending até o próximo gatilho).
    const result = await navigator.locks.request(
      getSyncLockName(),
      { ifAvailable: true },
      async (lock) => {
        if (!lock) return null;
        return doSync();
      },
    );
    if (result === null) {
      // Lock estava ocupado — espera a invocação ativa liberar e tenta
      // de novo. ``ifAvailable`` ausente faz a chamada esperar enfileirada.
      void navigator.locks.request(getSyncLockName(), async () => {
        await doSync();
      });
      return { synced: 0, failed: 0, remaining: 0 };
    }
    return result;
  }

  return doSync();
}

/**
 * Cancela uma entry de upload de forma segura, status-aware. Função
 * high-level que conhece o ciclo de vida (XHR/abort) — por isso vive aqui
 * e não no ``outbox.ts`` puro.
 *
 * - ``pending`` / ``blocked`` / ``failed``: remove direto.
 * - ``syncing``: aborta o XHR via ``AbortController`` registrado no
 *   ``inflightAborts``, aguarda o handler retornar (que vai cair no catch
 *   e voltar para ``pending``), e remove em seguida. Se a entry alcançou
 *   ``done`` antes do abort efetivar (race), o anexo já foi confirmado no
 *   backend — enfileira ``delete_anexo_peticao`` para limpar.
 * - ``done``: nada a fazer.
 */
export async function cancelSyncingEntry(entryId: string): Promise<void> {
  const initial = await getEntry(entryId);
  if (!initial) return;
  const entryType = initial.type;

  if (initial.status === 'syncing') {
    // Marca como cancelada antes do abort. ``processSingleEntry`` consulta
    // esse set ao escrever o status final e preserva ``failed`` mesmo que
    // o handler tenha completado em paralelo.
    cancelledEntries.add(entryId);
    const abort = inflightAborts.get(entryId);
    if (abort) {
      abort.abort();
      inflightAborts.delete(entryId);
    }
    // Aguarda a entry sair de ``syncing`` (handler cai no catch via abort,
    // ou completa e respeita o marcador). Polling com deadline.
    const deadline = Date.now() + 5000;
    let current = initial;
    while (current.status === 'syncing' && Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 50));
      const next = await getEntry(entryId);
      if (!next) return;
      current = next;
    }

    // Caso (raro): handler ainda vivo após 5s — não removemos entry/blob,
    // pois o handler pode chamar confirm a qualquer momento. Marca como
    // ``failed`` com sinal explícito; o ``cancelledEntries`` permanece
    // (será consumido quando o handler finalmente terminar).
    if (current.status === 'syncing') {
      await updateEntryStatus(
        entryId,
        'failed',
        'Cancelamento aguardando handler — verifique conectividade',
      );
      if (current.payload.peticao_id) {
        await markBlockedByPeticao(
          current.payload.peticao_id,
          'Cancelamento em andamento',
        );
      }
      return;
    }

    // Se o upload chegou a confirmar (entry === 'done'), o backend já
    // tem o recurso — precisa enfileirar a operação de remoção compatível
    // com o tipo da entry original.
    if (current.status === 'done') {
      if (entryType === 'upload_anexo_peticao') {
        const p = current.payload as Partial<UploadAnexoPeticaoPayload>;
        if (p.peticao_id && p.client_upload_id) {
          await addEntry({
            type: 'delete_anexo_peticao',
            payload: {
              peticao_id: p.peticao_id,
              client_upload_id: p.client_upload_id,
            },
            description: 'Cancelar anexo já enviado',
          });
        }
      }
      // Para upload_principal_peticao não há endpoint de "remover principal"
      // — a substituição é responsabilidade do próximo upload, que dispara
      // cleanup do S3 anterior no service.
    }
  }

  // Remove a entry e limpa o blob associado (status terminal: pending,
  // blocked, failed, ou done). Limpa também o marcador de cancelamento.
  const finalEntry = await getEntry(entryId);
  if (finalEntry && finalEntry.status !== 'syncing') {
    const blobRef = (finalEntry.payload as { blob_ref?: string }).blob_ref;
    if (blobRef) {
      await deleteBlob(blobRef);
    }
    await removeEntry(entryId);
  }
  cancelledEntries.delete(entryId);
}

/**
 * Starts automatic sync:
 *   - on window "online" event
 *   - immediately if already online at boot
 *
 * Returns a cleanup function that removes the listener.
 */
export function startAutoSync(): () => void {
  const onOnline = () => {
    processOutbox();
  };

  window.addEventListener('online', onOnline);

  if (navigator.onLine) {
    processOutbox();
  }

  return () => {
    window.removeEventListener('online', onOnline);
  };
}
